import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AdminModal, INPUT, errText } from "@/components/pageSettings/AdminModal";
import { ThankYouTestPanel } from "./ThankYouTestPanel";

const MIN_VIEWS = 20;
const COLS = [["views", "Views"], ["call", "Call taps"], ["text", "Text taps"], ["article", "Article taps"]];

function StatsTable({ rows, defaultKey }) {
  const best = rows.find((r) => r.views >= MIN_VIEWS && r.tap_rate != null);
  if (!rows.length) {
    return <p className="py-6 text-sm text-zinc-500" data-testid="ty-stats-empty">No thank-you page visits in this period yet.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="ty-stats-table">
        <thead>
          <tr className="border-b-2 border-black text-left label-mono">
            <th className="py-2 pr-3">Design</th>
            {COLS.map(([k, label]) => <th key={k} className="py-2 pr-3 text-right">{label}</th>)}
            <th className="py-2 text-right">Tap rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b border-zinc-200" data-testid={`ty-stats-row-${r.key}`}>
              <td className="py-2 pr-3 font-bold">
                {r.name}
                {r.key === defaultKey && <span className="ml-2 border border-black px-1 text-[10px] uppercase">Default</span>}
                {best?.key === r.key && <span className="ml-2 bg-emerald-600 px-1 text-[10px] uppercase text-white" data-testid="ty-stats-best">Best</span>}
              </td>
              {COLS.map(([k]) => <td key={k} className="py-2 pr-3 text-right tabular-nums" data-testid={`ty-stats-${r.key}-${k}`}>{r[k]}</td>)}
              <td className="py-2 text-right tabular-nums" data-testid={`ty-stats-${r.key}-rate`}>{r.tap_rate == null ? "—" : `${r.tap_rate}%`}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-[12px] text-zinc-500">
        Tap rate = call + text + article taps per 100 thank-you views. A design needs {MIN_VIEWS}+ views before it can be marked Best.
      </p>
    </div>
  );
}

export function ThankYouStats({ onClose }) {
  const [days, setDays] = useState(30);
  const [buyer, setBuyer] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    setData(null);
    api.get("/admin/thankyou-stats", { params: { days, buyer: buyer || undefined } })
      .then((r) => { if (live) setData(r.data); })
      .catch((e) => { if (live) { toast.error(errText(e, "Couldn't load the stats")); setData({ designs: [], buyers: [] }); } });
    return () => { live = false; };
  }, [days, buyer]);
  return (
    <AdminModal eyebrow="Thank-you pages" title="Thank-you click stats" onClose={onClose} testId="ty-stats-modal" wide>
      <ThankYouTestPanel />
      <div className="mb-4 flex flex-wrap gap-3">
        <label className="block">
          <span className="label-mono">Period</span>
          <select className={INPUT} value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="ty-stats-days">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </label>
        <label className="block min-w-[220px]">
          <span className="label-mono">Buyer</span>
          <select className={INPUT} value={buyer} onChange={(e) => setBuyer(e.target.value)} data-testid="ty-stats-buyer">
            <option value="">All buyers</option>
            {(data?.buyers || []).map((b) => <option key={b.code} value={b.code}>{b.firm_name}</option>)}
            {buyer && !(data?.buyers || []).some((b) => b.code === buyer) && <option value={buyer}>{buyer}</option>}
          </select>
        </label>
      </div>
      {data ? <StatsTable rows={data.designs} defaultKey={data.default} /> : <p className="py-6 text-sm text-zinc-500" data-testid="ty-stats-loading">Loading…</p>}
    </AdminModal>
  );
}
